// FlixDetailReaction.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import getCanShare from '../resource/getCanShare.js';
import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import Loading from '../component/Loading.jsx';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { textColor, scrollBarHiddenPadding } from '../style/variables.js';
import Shake from '../style/Shake.js';
import media from '../style/media.js';

import UserAvatarWithStatus from '../container/UserAvatarWithStatus.js';
import UserStatusIndicator from '../container/UserStatusIndicator.js';
import NeedAuth from '../container/NeedAuth.js';
import WithEditMessage from '../container/WithEditMessage.js';
import WithHideMessage from '../container/WithHideMessage.js';

import useIsClient from '../hook/useIsClient.js';
import useShortShareLink, { ShareType } from '../hook/useShortShareLink.js';

import { getIsChinaPartner } from '../resource/partner.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { EMPTY_ARRAY, NULL_FUNCTION } from '../resource/defaults.js';

import ChatIconResource from '../../img/btn_chat_24.svg';
import CopyIconResource from '../../img/ic_album.svg';
import ShareIconResource from '../../img/ic-share-20.svg';
import LikeIconResource from '../../img/btn_rating_like.svg';
import DislikeIconResource from '../../img/btn_rating_unlike.svg';
import ActiveLikeIconResource from '../../img/btn_rating_like_active.svg';
import ActiveDislikeIconResource from '../../img/btn_rating_unlike_active.svg';
import GiftIconResource from '../../img/ic_gift_fill.svg';

import { ButtonId } from '../resource/trackingConstants.js';

const isChinaPartner = getIsChinaPartner();
const canShare = getCanShare();

export const FlixDetailReaction = ({
  meId = null,
  messageId = '',
  senderId = null,
  senderUsername = '',
  displayedSenderUsername = '',
  unlockPrice = null,
  ratedAs = null,
  likeCount = 0,
  isFollowing = false,
  isPublished = true,
  shouldShowUser = false,
  referralCode = '',
  referralSignupRewardDiamond = 0,
  shouldFetchReferral = false,
  shareI18nVideoTemplates = EMPTY_ARRAY,
  login = NULL_FUNCTION,
  fetchMeReferral = NULL_FUNCTION,
  sendChatStart = NULL_FUNCTION,
  submitMessageRating = NULL_FUNCTION,
  setGiftPanel = NULL_FUNCTION,
  sendFollowUser = NULL_FUNCTION,
  copyTextWithToastr = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const isSender = meId && meId === senderId;
  const isClient = useIsClient();
  const shouldHideChat = true;
  const trackingPayload = {
    abData: {
      unlockPrice,
    },
  };

  useEffect(() => {
    if (shouldFetchReferral) {
      fetchMeReferral();
    }
  }, [fetchMeReferral, shouldFetchReferral]);

  const getShareTitleKey = () => {
    const randomIndex = Math.floor(
      Math.random() * shareI18nVideoTemplates.length
    );
    return shareI18nVideoTemplates[randomIndex];
  };

  const handleShare = shortShareLink => {
    const shareText = t(getShareTitleKey(), {
      ns: TranslationNamespace.GENERAL,
      username: senderUsername,
      referral_code: referralCode,
      reward_diamond: referralSignupRewardDiamond,
    });
    navigator
      .share({
        title: 'SWAG',
        text: shareText,
        url: shortShareLink,
      })
      .catch(() => null);
  };
  const [isFetchingShareLink, onClickShare] = useShortShareLink({
    targetId: messageId,
    shareType: ShareType.POST,
    onShortShareLinkFetched: handleShare,
  });
  const handleCopy = shortShareLink => {
    copyTextWithToastr({
      copiedText: shortShareLink,
      successfulTextKey: 'copy_to_clipboard_successfully',
      failedTextKey: 'copy_to_clipboard_failed',
      textKeyNamespace: TranslationNamespace.NOTIFICATION,
    });
  };
  const [isFetchingCopyLink, onClickCopy] = useShortShareLink({
    targetId: messageId,
    shareType: ShareType.POST,
    onShortShareLinkFetched: handleCopy,
  });

  const handleClickRating = ({ isLike }) => {
    return submitMessageRating({ rating: isLike ? 100 : 0 });
  };
  const handleClickFollow = () => {
    return sendFollowUser({ id: senderId });
  };
  const handleClickChat = () => {
    return sendChatStart({ userId: senderId });
  };
  const handleClickGift = () => {
    setGiftPanel({ isOpen: true });
    return sendChatStart({ userId: senderId });
  };

  const renderActionButton = ({
    element,
    mixpanelButtonId,
    shouldChooseEntryOption = false,
  }) => {
    return (
      <NeedAuth>
        {React.cloneElement(element, {
          'data-key': 'needAuth',
          'data-element_id': mixpanelButtonId,
          'data-tracking_payload': trackingPayload,
          onClick: async () => {
            if (shouldChooseEntryOption) {
              try {
                await login();
              } catch {
                return;
              }
              element.props?.onClick?.();
            } else {
              login({ shouldChooseEntryOption: false });
            }
          },
        })}
        {React.cloneElement(element, {
          'data-key': 'authing',
          onClick: () => null,
        })}
        {React.cloneElement(element, {
          'data-key': 'authed',
        })}
      </NeedAuth>
    );
  };

  return (
    <StyledFlixDetailReaction>
      <ActionButtonSection>
        <ActionButtonsScrollerWrapper>
          <ActionButtonsWrapper>
            <ActionButtons>
              {isPublished &&
                renderActionButton({
                  mixpanelButtonId: ButtonId.All.ButtonLike,
                  shouldChooseEntryOption: true,
                  element: (
                    <ActionButton
                      data-element_id={ButtonId.All.ButtonLike}
                      data-tracking_payload={trackingPayload}
                      onClick={() => handleClickRating({ isLike: true })}
                    >
                      <ActionIcon
                        src={
                          ratedAs === 100
                            ? ActiveLikeIconResource
                            : LikeIconResource
                        }
                        alt="Like"
                        aria-hidden
                      />
                      {likeCount
                        ? likeCount
                        : t('general_like', {
                            ns: TranslationNamespace.POST,
                          })}
                    </ActionButton>
                  ),
                })}
              {isPublished &&
                renderActionButton({
                  mixpanelButtonId: ButtonId.All.ButtonDislike,
                  shouldChooseEntryOption: true,
                  element: (
                    <ActionButton
                      data-element_id={ButtonId.All.ButtonDislike}
                      data-tracking_payload={trackingPayload}
                      onClick={() => handleClickRating({ isLike: false })}
                    >
                      <ActionIcon
                        src={
                          ratedAs === 0
                            ? ActiveDislikeIconResource
                            : DislikeIconResource
                        }
                        alt="Dislike"
                        aria-hidden
                      />
                      {t('general_dislike', {
                        ns: TranslationNamespace.POST,
                      })}
                    </ActionButton>
                  ),
                })}
              {isPublished && !isSender && (
                <ActionButton
                  data-element_id={ButtonId.All.ButtonChat}
                  data-tracking_payload={trackingPayload}
                  onClick={handleClickChat}
                >
                  <ActionIcon src={ChatIconResource} alt="Chat" aria-hidden />
                  {t('tabbar_chat', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ActionButton>
              )}
              {isPublished && !isSender && (
                <ActionButton
                  data-element_id={ButtonId.All.ButtonGift}
                  data-tracking_payload={trackingPayload}
                  onClick={handleClickGift}
                >
                  <ActionIcon src={GiftIconResource} alt="Gift" aria-hidden />
                  {t('gift_title', {
                    ns: TranslationNamespace.POST,
                  })}
                </ActionButton>
              )}
              {isPublished && isClient && canShare && !isChinaPartner && (
                <ActionButton
                  data-element_id={ButtonId.All.ButtonShare}
                  data-tracking_payload={{ messageId }}
                  onClick={onClickShare}
                  disabled={isFetchingShareLink}
                >
                  {isFetchingShareLink ? (
                    <Loading />
                  ) : (
                    <ActionIcon
                      src={ShareIconResource}
                      alt="Share"
                      aria-hidden
                    />
                  )}
                  {t('general_share', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ActionButton>
              )}
              {isPublished && !isChinaPartner && !canShare && (
                <ActionButton
                  data-element_id={ButtonId.All.ButtonShare}
                  data-tracking_payload={{ messageId }}
                  onClick={onClickCopy}
                  disabled={isFetchingCopyLink}
                >
                  {isFetchingCopyLink ? (
                    <Loading />
                  ) : (
                    <ActionIcon src={CopyIconResource} alt="Copy" />
                  )}
                  {t('general_copy', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </ActionButton>
              )}
              {!isPublished &&
                isSender &&
                renderActionButton({
                  mixpanelButtonId: ButtonId.All.ButtonEdit,
                  element: (
                    <WithEditMessage messageId={messageId}>
                      {({ handleOnClick, iconSource }) => (
                        <ActionButton
                          data-element_id={ButtonId.All.ButtonEdit}
                          data-tracking_payload={{
                            'message.id': messageId,
                          }}
                          onClick={handleOnClick}
                        >
                          <ActionIcon
                            src={iconSource}
                            alt={t('edit', {
                              ns: TranslationNamespace.POST,
                            })}
                          />
                          {t('edit', {
                            ns: TranslationNamespace.POST,
                          })}
                        </ActionButton>
                      )}
                    </WithEditMessage>
                  ),
                })}
              {!isPublished &&
                renderActionButton({
                  mixpanelButtonId: ButtonId.All.ButtonHiddenMassage,
                  element: (
                    <WithHideMessage messageId={messageId}>
                      {({ handleOnClick, iconSource }) => (
                        <ActionButton
                          data-element_id={ButtonId.All.ButtonHiddenMassage}
                          data-tracking_payload={{
                            'message.id': messageId,
                          }}
                          onClick={handleOnClick}
                        >
                          <ActionIcon
                            src={iconSource}
                            alt={t('hide', {
                              ns: TranslationNamespace.POST,
                            })}
                          />
                          {t('hide', {
                            ns: TranslationNamespace.POST,
                          })}
                        </ActionButton>
                      )}
                    </WithHideMessage>
                  ),
                })}
            </ActionButtons>
          </ActionButtonsWrapper>
        </ActionButtonsScrollerWrapper>
      </ActionButtonSection>
      {shouldShowUser && (
        <>
          <Divider />
          <UserSection shouldHideChat={shouldHideChat}>
            <AvatarWrapper>
              <UserAvatarWithStatus id={senderId} size={28} circleOffset={2} />
            </AvatarWrapper>
            <UsernameWrapper>
              <Username
                to={`/user/${senderId}/flix`}
                data-element_id={ButtonId.Detail.ContentDetailName}
                data-tracking_payload={{
                  'user.id': senderId,
                  'user.username': senderUsername,
                }}
              >
                {displayedSenderUsername}
              </Username>
              <UserStatusWrapper>
                <UserStatusIndicator
                  userId={senderId}
                  shouldShowLiveStream
                  shouldShowOnline
                />
              </UserStatusWrapper>
              {!isFollowing && (
                <FollowButton
                  data-element_id={ButtonId.All.ButtonFollow}
                  data-tracking_payload={trackingPayload}
                  onClick={handleClickFollow}
                >
                  {t('follow', {
                    ns: TranslationNamespace.GENERAL,
                  })}
                </FollowButton>
              )}
            </UsernameWrapper>
            {!shouldHideChat && (
              <ChatButtonWrapper>
                <ChatButton
                  data-element_id={ButtonId.All.ButtonChat}
                  data-tracking_payload={trackingPayload}
                  onClick={handleClickChat}
                >
                  <ChatIcon src={ChatIconResource} />
                  {t('chat_with_me', {
                    ns: TranslationNamespace.POST,
                  })}
                </ChatButton>
                <GiftButton
                  data-element_id={ButtonId.All.ButtonGift}
                  data-tracking_payload={trackingPayload}
                  onClick={handleClickGift}
                >
                  <GiftIcon src={GiftIconResource} alt="Gift" />
                </GiftButton>
              </ChatButtonWrapper>
            )}
          </UserSection>
        </>
      )}
    </StyledFlixDetailReaction>
  );
};

FlixDetailReaction.propTypes = {
  meId: PropTypes.string,
  messageId: PropTypes.string,
  senderId: PropTypes.string,
  senderUsername: PropTypes.string,
  displayedSenderUsername: PropTypes.string,
  unlockPrice: PropTypes.number,
  ratedAs: PropTypes.number,
  likeCount: PropTypes.number,
  isFollowing: PropTypes.bool,
  isPublished: PropTypes.bool,
  shouldShowUser: PropTypes.bool,
  referralCode: PropTypes.string,
  referralSignupRewardDiamond: PropTypes.number,
  shouldFetchReferral: PropTypes.bool,
  login: PropTypes.func,
  sendChatStart: PropTypes.func,
  submitMessageRating: PropTypes.func,
  setGiftPanel: PropTypes.func,
  sendFollowUser: PropTypes.func,
  copyTextWithToastr: PropTypes.func,
  shareI18nVideoTemplates: PropTypes.array,
  fetchMeReferral: PropTypes.func,
};

const StyledFlixDetailReaction = styled.div`
  width: inherit;
`;

const ActionButtonSection = styled.div`
  height: 64px;
  overflow-y: hidden;
`;

const ActionButtonsScrollerWrapper = styled.div`
  overflow-y: hidden;
  overflow-x: auto;
`;

const ActionButtonsWrapper = styled.div`
  padding-bottom: ${scrollBarHiddenPadding}px;
  display: inline-block;
`;

const ActionButtons = styled.div`
  display: inline-flex;
  flex-wrap: nowrap;
`;

const ActionButton = styled.button`
  ${ResetButtonStyle};
  border-radius: 50%;
  margin-right: 12px;
  width: 64px;
  height: 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${textColor.white};
  font-size: 12px;
  line-height: 1;
  transition: background-color 0.2s;
  :last-child {
    margin-right: 0px;
  }
  :active {
    background-color: rgba(100, 100, 100, 0.2);
  }
`;

const ActionIcon = styled.img`
  margin-bottom: 8px;
  width: 32px;
  height: 32px;
`;

const ChatIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 4px;
`;

const Divider = styled.div`
  margin-top: 12px;
  margin-bottom: 12px;
  height: 1px;
  background-color: rgba(100, 100, 100, 0.15);
`;

const UserSection = styled.div`
  padding-left: 16px;
  padding-right: 16px;
  display: grid;
  ${({ shouldHideChat }) => {
    if (shouldHideChat) {
      return css`
        grid-template-areas: 'avatar username';
      `;
    }
    return css`
      grid-template-areas:
        'avatar username'
        'avatar chat-button';
    `;
  }}
  grid-template-columns: auto 1fr;
  grid-column-gap: 12px;
  grid-row-gap: 12px;
`;

const AvatarWrapper = styled.div`
  grid-area: avatar;
`;

const UsernameWrapper = styled.div`
  grid-area: username;
  display: flex;
  align-items: center;
  overflow: hidden;
  color: ${textColor.white};
  font-size: 16px;
  ${media.mobile`
    font-size: 14px;
  `};
`;

const Username = styled(Link)`
  font-weight: 600;
  ${TextEllipsis};
`;

const UserStatusWrapper = styled.div`
  margin-left: 4px;
`;

const FollowButton = styled.button`
  ${ResetButtonStyle};
  font-weight: 600;
  flex: none;
  color: ${textColor.primary};
  font-size: 14px;
  ::before {
    content: '・';
    padding: 0px 4px;
    color: ${textColor.white};
    font-size: 14px;
    font-weight: 600;
  }
`;

const ChatButtonWrapper = styled.div`
  grid-area: chat-button;
  display: flex;
`;

const ChatButton = styled.button`
  ${ResetButtonStyle};
  border-radius: 18px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: auto;
  height: 36px;
  color: ${textColor.white};
  font-size: 14px;
  font-weight: 600;
  background-image: linear-gradient(
    103deg,
    #5d82bc,
    #00d2be,
    #5d82bc,
    #8e37d7,
    #5d82bc,
    #00d2be,
    #5d82bc
  );
  background-size: 300% 100%;
  @keyframes marquee {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: 0 0;
    }
  }
  animation: 1.4s infinite linear marquee;
`;

const GiftButton = styled.button`
  ${ResetButtonStyle};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-image: linear-gradient(
    135deg,
    #00d2be,
    rgb(93, 130, 188) 52%,
    #8e37d7
  );
`;

const GiftIcon = styled.img`
  height: 24px;
  width: auto;
  animation: 1.4s infinite linear ${Shake};
`;

export default FlixDetailReaction;
